import { useEffect, useState } from 'react';

const editable = () => document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
// Screen orientation is independent of keyboard-induced viewport resizing.
const orientationKey = () => Math.abs(window.screen?.orientation?.angle ?? window.orientation ?? 0) % 180;
const visibleHeight = () => Math.min(window.innerHeight, window.visualViewport?.height || window.innerHeight);

// Native events are authoritative while the keyboard opens. A measured shrink
// followed by restoration also ends that state if the native hide event is lost.
export function useMobileKeyboard(enabled) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!enabled) { setOpen(false); return undefined; }
    let disposed = false;
    let nativeOpen = false;
    let browserRestingHeight = window.innerHeight;
    const restingViewports = new Map([[orientationKey(), { height: visibleHeight(), width: window.innerWidth }]]);
    const shrunkOrientations = new Set();
    const viewport = window.visualViewport;
    const update = () => {
      if (disposed || document.visibilityState === 'hidden') return;
      const orientation = orientationKey();
      const height = visibleHeight();
      const known = restingViewports.get(orientation);
      const opposite = restingViewports.get(orientation === 0 ? 90 : 0);
      // Native resize changes height, not width. A matching quarter-turn of a
      // previously closed viewport supplies a baseline without learning the
      // keyboard-reduced height as "closed". If the geometry does not match
      // (for example split view), keep waiting for native hide/known geometry.
      const rotated = opposite && Math.abs(window.innerWidth - opposite.height) <= 24
        ? opposite.width : undefined;
      const resting = known?.height ?? rotated;
      if (nativeOpen && resting !== undefined) {
        if (resting - height > 120) shrunkOrientations.add(orientation);
        // Do not clear on willShow's initially unchanged viewport, focus loss,
        // navigation alone, or a rotation with no known closed-height evidence.
        if (shrunkOrientations.has(orientation) && height >= resting - 24) {
          nativeOpen = false;
          shrunkOrientations.clear();
        }
      }
      if (!nativeOpen && !editable()) {
        restingViewports.set(orientation, { height, width: window.innerWidth });
      }
      setOpen(nativeOpen || Boolean(editable() && browserRestingHeight - height > 120));
    };
    const orientationChanged = () => { browserRestingHeight = window.innerHeight; update(); };
    const listeners = [];
    const remove = handle => {
      try { Promise.resolve(handle?.remove()).catch(() => {}); } catch { /* Already removed by the shell. */ }
    };
    const subscribe = (plugin, event, callback) => {
      if (!plugin?.addListener) return;
      try {
        Promise.resolve(plugin.addListener(event, callback)).then(handle => {
          if (disposed) remove(handle); else listeners.push(handle);
        }).catch(() => { /* Browser lifecycle/viewport fallback remains active. */ });
      } catch { /* Older shells may not expose the plugin. */ }
    };
    const keyboard = window.Capacitor?.Plugins?.Keyboard;
    subscribe(keyboard, 'keyboardWillShow', () => { nativeOpen = true; shrunkOrientations.clear(); update(); });
    subscribe(keyboard, 'keyboardDidHide', () => { nativeOpen = false; shrunkOrientations.clear(); update(); });
    subscribe(window.Capacitor?.Plugins?.App, 'appStateChange', state => { if (state?.isActive) update(); });
    viewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', orientationChanged);
    window.addEventListener('pageshow', update);
    document.addEventListener('visibilitychange', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      disposed = true;
      listeners.forEach(remove);
      viewport?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', orientationChanged);
      window.removeEventListener('pageshow', update);
      document.removeEventListener('visibilitychange', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
    };
  }, [enabled]);
  return open;
}
